'use client';

import { useTranslations } from 'next-intl';
import {
  dayOfWeek,
  type DayOfWeek,
  type TimeSlot,
} from '@/constants/timeSlots';
import { groupSlotsByDay } from '@/constants/timeSlots';
import { cn } from '@/lib/utils';
import { Skeleton } from '../skeleton';

interface AvailabilityViewerProps {
  schedule: TimeSlot[];
  className?: string;
  timezone: string;
  isLoading?: boolean;
}

export default function AvailabilityViewer({
  schedule,
  className,
  timezone,
  isLoading = false,
}: AvailabilityViewerProps) {
  const t = useTranslations('AvailabilitySelector');
  if (isLoading) {
    return <AvailabilitySkeleton />;
  }
  if (schedule.length === 0) {
    return <div>{t('noAvailability')}</div>;
  }

  // Group slots by day for rendering
  const slotsByDay = groupSlotsByDay(schedule, timezone);

  return (
    <div className={cn('space-y-6', className)}>
      <div className='space-y-4 md:flex md:flex-wrap md:gap-8'>
        {dayOfWeek
          .filter((day) => slotsByDay[day].length > 0)
          .map((day) => (
            <DayScheduleItem
              key={day}
              day={day}
              slots={slotsByDay[day]}
              timezone={timezone}
            />
          ))}
      </div>
    </div>
  );
}

const DayScheduleItem = ({
  day,
  slots,
  timezone,
}: {
  day: DayOfWeek;
  slots: TimeSlot[];
  timezone: string;
}) => {
  const t = useTranslations('AvailabilitySelector');

  return (
    <div data-testid={`day-schedule-item-${day}`} className='space-y-3'>
      <div className='flex items-start gap-6 md:flex-col md:gap-4'>
        <h3 className='min-w-[100px] font-bold'>
          {t(`days.${day.toLowerCase()}`)}
        </h3>

        <div className='flex-1'>
          <div className='w-full md:flex-grow'>
            <TimeSlotList slots={slots} timezone={timezone} />
          </div>
        </div>
      </div>
    </div>
  );
};

const TimeSlotList = ({
  slots,
  timezone,
}: {
  slots: TimeSlot[];
  timezone: string;
}) => {
  return (
    <div className='space-y-4'>
      {slots.map((slot, index) => (
        <div key={index} className='flex items-center gap-2'>
          <span>
            {slot.from.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
              timeZone: timezone,
            })}
          </span>
          <span>-</span>
          <span>
            {slot.to.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
              timeZone: timezone,
            })}
          </span>
        </div>
      ))}
    </div>
  );
};

const AvailabilitySkeleton = () => {
  return (
    <div className='space-y-6'>
      <div className='space-y-4'>
        <div className='space-y-3'>
          <div className='flex items-start gap-3 md:gap-4'>
            <Skeleton className='h-5 w-1/4' />
            <div className='flex flex-1 flex-col gap-2'>
              <div className='w-full md:flex-grow'>
                <Skeleton className='h-5 w-full' />
              </div>
              <div className='w-full md:flex-grow'>
                <Skeleton className='h-5 w-full' />
              </div>
            </div>
          </div>
          <div className='flex items-start gap-3 md:gap-4'>
            <Skeleton className='h-5 w-1/4' />

            <div className='flex-1'>
              <div className='w-full md:flex-grow'>
                <Skeleton className='h-5 w-full' />
              </div>
            </div>
          </div>
          <div className='flex items-start gap-3 md:gap-4'>
            <Skeleton className='h-5 w-1/4' />

            <div className='flex-1'>
              <div className='w-full md:flex-grow'>
                <Skeleton className='h-5 w-full' />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
